import type { LucideIcon } from "lucide-react";
import { CreditCard, Heart, Home, Plane, UserCog } from "lucide-react";
import type { Translation } from "@/types/i18n";

/**
 * The traveller's navigation: Home, Trips, Health, Card and Account, plus
 * Emergency standing apart at the foot of the sidebar (UX_CONCEPT.md §4).
 *
 * Profile, Notifications, Appearance, Security and App Info are not
 * destinations: they are the account menu behind the avatar, so a URL there
 * belongs to none. Account holds what concerns the contract and the data —
 * the plan, payment, invoices, and Compliance.
 *
 * The sidebar used to list nine entries named after data types — health
 * records, medications, immunizations, documents — and left out the card, the
 * wallet and the subscription altogether. Destinations are named after what
 * the traveller came to do; the pages that used to be top-level entries are
 * the `pages` of a destination and appear as tabs above it, so none of them
 * became unreachable when it left the sidebar.
 *
 * Which destination a URL belongs to is decided here, once, for the sidebar,
 * the bottom tab bar and the section tabs alike.
 */

export type DestinationId = "home" | "trips" | "health" | "card" | "account";

export interface NavPage {
  path: string;
  label: (t: Translation) => string;
}

export interface Destination {
  id: DestinationId;
  icon: LucideIcon;
  label: (t: Translation) => string;
  /** Where the destination itself lands. */
  path: string;
  /** Paths matched exactly. */
  exact?: string[];
  /** Path prefixes this destination owns; a longer prefix elsewhere wins. */
  owns: string[];
  /** Shown as tabs above the page when there is more than one. */
  pages: NavPage[];
}

/** Emergency is not a destination: it is reachable from everywhere. */
export const EMERGENCY_PATH = "/traveler/health/emergency";

export const DESTINATIONS: Destination[] = [
  {
    id: "home",
    icon: Home,
    label: (t) => t.navHome,
    path: "/dashboard",
    // "/" and "/health" render the dashboard too (App.tsx).
    exact: ["/", "/health"],
    owns: ["/dashboard"],
    pages: [],
  },
  {
    id: "trips",
    icon: Plane,
    label: (t) => t.navTrips,
    path: "/traveler/travels",
    owns: [
      "/traveler/travels",
      "/traveler/travel",
      "/traveler/health-recommendations",
    ],
    pages: [],
  },
  {
    id: "health",
    icon: Heart,
    label: (t) => t.navHealth,
    path: "/traveler/health",
    owns: [
      "/traveler/health",
      "/traveler/medications",
      "/traveler/immunizations",
      "/traveler/documents",
      "/traveler/chat",
    ],
    pages: [
      { path: "/traveler/health", label: (t) => t.overview },
      { path: "/traveler/medications", label: (t) => t.medications },
      { path: "/traveler/immunizations", label: (t) => t.immunizations },
      { path: "/traveler/health/appointment", label: (t) => t.appointments },
      { path: "/traveler/documents", label: (t) => t.documents },
      { path: "/traveler/chat", label: (t) => t.navAssistant },
    ],
  },
  {
    id: "card",
    icon: CreditCard,
    label: (t) => t.navCard,
    path: "/traveler/card",
    owns: [
      "/traveler/card",
      "/traveler/med-access-requests",
      "/traveler/health/checkin",
    ],
    pages: [
      { path: "/traveler/card", label: (t) => t.navCard },
      { path: "/traveler/card/wallet", label: (t) => t.navWallet },
      { path: "/traveler/card/shares", label: (t) => t.navShares },
      {
        path: "/traveler/med-access-requests",
        label: (t) => t.navAccessRequests,
      },
      { path: "/traveler/health/checkin", label: (t) => t.navCheckIn },
    ],
  },
  {
    id: "account",
    icon: UserCog,
    label: (t) => t.navAccount,
    path: "/traveler/subscription",
    owns: [
      "/traveler/subscription",
      "/subscription",
      "/traveler/account",
      "/traveler/consent-requests",
      "/settings/consents",
      "/settings/gdpr",
    ],
    pages: [
      { path: "/traveler/subscription", label: (t) => t.navSubscriptions },
      {
        path: "/traveler/subscription/settings",
        label: (t) => t.navPayment,
      },
      { path: "/traveler/subscription/billing", label: (t) => t.navInvoices },
      { path: "/traveler/account/compliance", label: (t) => t.navCompliance },
    ],
  },
];

/** True when `pathname` is `prefix` or lies below it. */
export function isUnder(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + "/");
}

/** Length of the longest of `prefixes` that `pathname` lies under, or -1. */
function longestMatch(pathname: string, prefixes: string[]): number {
  return prefixes.reduce(
    (best, prefix) =>
      isUnder(pathname, prefix) ? Math.max(best, prefix.length) : best,
    -1,
  );
}

export function isEmergencyPath(pathname: string): boolean {
  return isUnder(pathname, EMERGENCY_PATH);
}

/**
 * The destination a URL belongs to, or null for Emergency and for pages that
 * belong to none (partner and back-office pages, the demo).
 *
 * The longest owned prefix wins, which is how /traveler/health/checkin goes to
 * the card while the rest of /traveler/health stays with Health.
 */
export function destinationFor(pathname: string): Destination | null {
  if (isEmergencyPath(pathname)) {
    return null;
  }
  let best: Destination | null = null;
  let bestLength = -1;
  for (const destination of DESTINATIONS) {
    const length = destination.exact?.includes(pathname)
      ? Number.MAX_SAFE_INTEGER
      : longestMatch(pathname, destination.owns);
    if (length > bestLength) {
      best = destination;
      bestLength = length;
    }
  }
  return best;
}

/** The tab within a destination that `pathname` is on, or null. */
export function activePage(
  destination: Destination,
  pathname: string,
): NavPage | null {
  let best: NavPage | null = null;
  let bestLength = -1;
  for (const page of destination.pages) {
    if (isUnder(pathname, page.path) && page.path.length > bestLength) {
      best = page;
      bestLength = page.path.length;
    }
  }
  return best;
}
